// Updated TokenForm.js with sponsor/club precheck before PayPal
import React, { useState, useContext, useEffect, useRef } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "react-toastify";
import { DonationContext } from "../../context/DonationContext";
import { useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import { Link } from "react-router-dom";

const TokenForm = ({ hideClubSelector = false, defaultClubId = null }) => {
  const { t } = useContext(LanguageContext);
  const { triggerRefresh } = useContext(DonationContext);

  const [basePrice, setBasePrice] = useState(0);
  const [tokensSold, setTokensSold] = useState(0);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const sponsorIdRef = useRef(null);
  const clubIdRef = useRef(null);

  const [mode, setMode] = useState("euros");
  const [clubMode, setClubMode] = useState("existing");
  
  const navigate = useNavigate();
  
  const [isSpecial, setIsSpecial] = useState(false);

  const [formData, setFormData] = useState({
    euros: "",
    tokens: "",
    recipient: defaultClubId || "",
    newClubName: "",
    sponsorName: "",
    sponsorEmail: "",
    sponsorPassword: "",
    sponsorCompany: "",
    sponsorProduct: "",
  });

  const loadClubOptions = async (inputValue) => {
    if (!inputValue) return [];

    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/clubs?search=${inputValue}`);
    const data = await res.json();

    return data.map(club => ({
      label: club.name,
      value: club.id,
    }));
  };

  const [loadingDirect, setLoadingDirect] = useState(false);

  useEffect(() => {
    const checkLoginStatus = async () => {
      const token = localStorage.getItem("token");
      setIsLoggedIn(!!token);

      if (token) {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (res.ok && data.role === 'sponsor') {
            setIsSpecial(data.is_special);
          }
        } catch (err) {
          console.error("Error fetching profile:", err);
        }
      }
    };

    // Run on component mount
    checkLoginStatus();

    // Run when localStorage changes (e.g., logout from another tab)
    const handleStorage = () => checkLoginStatus();
    window.addEventListener("storage", handleStorage);

    // Run when tab becomes visible (e.g., user returns after logging out)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkLoginStatus();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    


    return () => {
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, clubsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/token/metrics`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/clubs`),
        ]);
        const metrics = await metricsRes.json();
        const clubsData = await clubsRes.json();
        if (metricsRes.ok) {
          setBasePrice(metrics.base_price);
          setTokensSold(metrics.tokens_sold);
        }
        if (clubsRes.ok) setClubs(clubsData);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (defaultClubId) {
      setFormData((prev) => ({ ...prev, recipient: defaultClubId }));
    }
  }, [defaultClubId]);

  const millionsSold = Math.floor(tokensSold / 1_000_000);
  const newPrice = parseFloat((basePrice + millionsSold * 0.001).toFixed(3));

  const calculatedTokens =
    mode === "euros"
      ? parseFloat(formData.euros || 0) / newPrice
      : parseFloat(formData.tokens || 0);

  const calculatedAmount =
    mode === "tokens"
      ? parseFloat(formData.tokens || 0) * newPrice
      : parseFloat(formData.euros || 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    const hasValidAmount = calculatedAmount > 0;
    const hasClub =
      clubMode === "existing"
        ? !!formData.recipient
        : formData.newClubName;
    const hasSponsorInfo = isLoggedIn
      ? true
      : formData.sponsorName &&
        formData.sponsorEmail &&
        formData.sponsorPassword;


    return hasValidAmount && hasClub && hasSponsorInfo;
  };

  const precheckAndPrepare = async () => {
    try {
      const payload = {
        sponsorName: isLoggedIn ? undefined : formData.sponsorName,
        sponsorEmail: isLoggedIn ? undefined : formData.sponsorEmail,
        sponsorPassword: isLoggedIn ? undefined : formData.sponsorPassword,
        sponsorCompany: isLoggedIn ? undefined : formData.sponsorCompany,
        sponsorProduct: isLoggedIn ? undefined : formData.sponsorProduct,
        clubName: clubMode === "new" ? formData.newClubName : undefined,        
        existingClubId: clubMode === "existing" ? formData.recipient : undefined,
      };
      const token = localStorage.getItem("token");

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/precheck`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        sponsorIdRef.current = data.sponsorId;
        clubIdRef.current = data.clubId;
        return true;
      } else {
        toast.error(data.error || t("precheck_failed"));

        return false;
      }
    } catch (error) {
      console.error("Precheck error:", error);
      toast.error(t("server_error_precheck"));
      return false;
    }
  };

  const donateAfterPayment = async (details) => {
    const token = localStorage.getItem("token");
    const payload = {
      ...details,
      tokens: calculatedTokens,
      amount: calculatedAmount,
      sponsorId: sponsorIdRef.current,
      recipientId: clubIdRef.current,
    };

    // Add new club data if user invited a new club
    if (clubMode === "new") {
      payload.clubName = formData.newClubName;
      payload.isNewClub = true;
    }
    console.log("Token used in donation:", token);

    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/donate`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(t("donation_success"));
        triggerRefresh();
        setFormData({
          euros: "",
          tokens: "",
          recipient: "",
          newClubName: "",          
          sponsorName: "",
          sponsorEmail: "",
          sponsorPassword: "",
          sponsorCompany: "",
          sponsorProduct: "",
        });
        navigate("/donation-confirmation");

      } else {
        toast.error(data.error || t("donation_failed"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("server_error"));
    }
  };

  const donateDirectly = async () => {
    if (!isFormValid()) {
      toast.error(t("fill_required_fields"), { position: "top-right" });
      return;
    }

    setLoadingDirect(true); // start loading

    const ok = await precheckAndPrepare();
    if (!ok) {
      setLoadingDirect(false);
      return;
    }



    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const payload = {
      amount: calculatedAmount,
      recipientId: clubIdRef.current
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/donate/direct`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(t("donation_success"));
        triggerRefresh();
        setFormData({
          euros: "",
          tokens: "",
          recipient: "",
          newClubName: "",          
          sponsorName: "",
          sponsorEmail: "",
          sponsorPassword: "",
          sponsorCompany: "",
          sponsorProduct: "",
        });
        navigate("/donation-confirmation");
      } else {
        toast.error(data.message || t("donation_failed"));
      }
    } catch (err) {
      console.error("Direct donation error:", err);
      toast.error(t("server_error"));
    }
    finally {
      setLoadingDirect(false); // end loading
    }
  };

const clubOptions = clubs.map(club => ({
  value: club.id,
  label: club.name,
}));

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-6 bg-white rounded-md shadow-md">
      <div className="bg-gray-50 p-4 rounded-md shadow-sm mb-6 border">
  <h2 className="text-xl font-semibold mb-2">
    {t("teamcoin_intro_heading")}
  </h2>
  <p className="mb-2">
    {t("teamcoin_intro_line1")}
  </p>
  <p className="mb-2">
    {t("teamcoin_intro_line2")}
  </p>
  <p>
    {t("teamcoin_intro_before_link")}{" "}
    <Link to="/clubs" className="text-blue-600 underline hover:text-blue-800">
      {t("teamcoin_intro_link")}
    </Link>
    {t("teamcoin_intro_after_link")}
  </p>
</div>
      <h2 className="text-2xl font-bold mb-6">{t("donate_tokens")}</h2>

      {loading ? (
        <p>{t("loading")}...</p>
      ) : (
        <form className="space-y-6">
          {/* Mode Selection */}
          <div>
            <label className="block font-semibold mb-2">
              {t("donation_method")}
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="mode"
                  value="tokens"
                  checked={mode === "tokens"}
                  onChange={() => setMode("tokens")}
                />
                {t("donate_by_tokens")}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="mode"
                  value="euros"
                  checked={mode === "euros"}
                  onChange={() => setMode("euros")}
                />
                {t("donate_by_euros")}
              </label>
            </div>
          </div>

          {/* Amount Input + Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block font-semibold mb-2">
                {mode === "tokens" ? t("number_of_tokens") : t("donation_in_euros")}
              </label>
              <input
                type="number"
                name={mode}
                value={formData[mode]}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                placeholder={mode === "tokens" ? "e.g. 100" : "e.g. 10.00"}
              />
            </div>            

            <div className="bg-gray-50 p-4 rounded-md text-sm border h-full">
              <p><strong>{t("current_price")}:</strong> €{newPrice.toFixed(3)}</p>
              <p><strong>{t("total_tokens")}:</strong> {calculatedTokens.toFixed(2)}</p>
              <p><strong>{t("total_amount")}:</strong> {calculatedAmount.toFixed(3)}</p>
            </div>
          </div>



          {/* Club Selection */}
          {!hideClubSelector && (
          <div>
            <label className="block font-semibold mb-2">{t("choose_club")}</label>
            <div className="flex gap-6 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="clubMode"
                  value="existing"
                  checked={clubMode === "existing"}
                  onChange={() => setClubMode("existing")}
                />
                {t("select_existing_club")}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="clubMode"
                  value="new"
                  checked={clubMode === "new"}
                  onChange={() => setClubMode("new")}
                />
                {t("invite_new_club")}
              </label>
            </div>

            {clubMode === "existing" ? (
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadClubOptions}
                value={
                  formData.recipient
                    ? { value: formData.recipient, label: clubs.find(c => c.id === formData.recipient)?.name || "" }
                    : null
                }
                onChange={(selectedOption) =>
                  setFormData((prev) => ({
                    ...prev,
                    recipient: selectedOption ? selectedOption.value : "",
                  }))
                }
                placeholder={t("select_a_club")}
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
              />


            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="newClubName"
                  value={formData.newClubName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                  placeholder={t("club_name")}
                />
                
              </div>
            )}
          </div>
)}
          {/* Sponsor Info */}
          {!isLoggedIn && (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">{t("sponsor_info")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="sponsorName"
                  value={formData.sponsorName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                  placeholder={t("your_full_name")}
                />
                <input
                  type="email"
                  name="sponsorEmail"
                  value={formData.sponsorEmail}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                  placeholder={t("your_email")}
                />
                <input
                  type="password"
                  name="sponsorPassword"
                  value={formData.sponsorPassword}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                  placeholder={t("your_password")}
                />
                <input
                  type="text"
                  name="sponsorCompany"
                  value={formData.sponsorCompany}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                  placeholder="Company Name"
                />
                <input
                  type="text"
                  name="sponsorProduct"
                  value={formData.sponsorProduct || ""}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md shadow-sm"
                  placeholder="Product/Brand Name (optional)"
                />
              </div>
            </div>

          )}

          {/* PayPal Button */}
          {/* Donation Action */}
          <div className="mt-6 flex justify-center">
            {isSpecial ? (
              <button
                type="button"
                onClick={donateDirectly}
                className={`bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 flex items-center justify-center ${
    loadingDirect ? "opacity-50 cursor-not-allowed" : ""
  }`}
              >
                {t("donate_without_paypal")}
              </button>
            ) : (
            <div className="w-full max-w-xs mx-auto">

              <PayPalButtons
                style={{ layout: "vertical", color: "blue", shape: "rect", label: "paypal" }}
                createOrder={async (data, actions) => {
                  if (!isFormValid()) {
                    toast.error(t("fill_required_fields"), { position: "top-right" });
                    return;
                  }

                  const ok = await precheckAndPrepare();
                  if (!ok) return;

                  return actions.order.create({
                    purchase_units: [
                      {
                        amount: {
                          value: calculatedAmount.toFixed(2),
                          currency_code: "EUR",
                        },
                      },
                    ],
                  });
                }}
                onApprove={async (data, actions) => {
                  const details = await actions.order.capture();
                  await donateAfterPayment(details);
                }}
                onError={(err) => {
                  console.error("PayPal error", err);
                  toast.error(t("paypal_payment_failed"), { position: "top-right" });
                }}
              />
              </div>
            )}
          </div>


        </form>
      )}
    </div>
  );
};

export default TokenForm;
